/**
 * Product list filters: the state kept in localStorage ("FiltersForProducts"), the status tabs,
 * the "Added" window, the one search box and the GET /product/products params built from them.
 * Pure functions only, so the page and its tests share one source of truth.
 */
import { categoryParams, CategoryOption } from "../categoryFilter";
import { createdAfterForKpiMonths } from "../productKpiWindow";

export const PRODUCT_FILTERS_KEY = "FiltersForProducts";
export const DEFAULT_PRODUCT_PAGE_SIZE = 10;

/** Product list views opened from the dashboard counts (label used in "Showing … products"). */
export const PRODUCT_LIST_VIEWS: Record<string, string> = {
  drafted: "drafted",
  published: "published",
  transferredIn: "transferred-in",
  transferredOut: "transferred-out",
};

export type Option = { label: string; value: string };
export type SearchScope = "all" | "name" | "xuid" | "productId" | "tags" | "color";
export type AddedPreset = "any" | "7d" | "30d" | "3m" | "custom";
export type AddedWindow = { preset: AddedPreset; from?: string; to?: string };
export type StatusTab = "all" | "drafted" | "published" | "inStock" | "outOfStock" | "needsPhoto";

export type ProductListFilters = {
  productName?: string;
  ProductId?: string;
  xuid?: string;
  tags?: string;
  /** Type-tag ids from the managed pick-list (any-of). Separate from the free-text `tags`. */
  typeTagIds?: string[];
  /** Only products the tag backfill left for a person to confirm. */
  typeTagReview?: boolean;
  color?: string;
  /** Karkhana loom ids (any-of); a renamed loom keeps its id. */
  loomIds?: string[];
  q?: string;
  includeAiSuggestions?: boolean;
  searchScope?: SearchScope;
  category?: CategoryOption | "";
  artform?: Option | "";
  type?: Option | "";
  subCategory?: Option | "";
  weaveType?: Option | "";
  price?: Record<string, any>;
  inStock?: { label: string; value: string } | "";
  view?: string;
  added?: AddedWindow;
  createdAfter?: string;
  createdBefore?: string;
  hasDraftChanges?: boolean;
  /** "Needs photo" tab (photo updates on): open photo requests and live pieces with no photo. */
  needsPhoto?: boolean;
  /** Narrows the Needs photo tab to one reason (no_photo, dyed_now, ...); empty = all. */
  photoReason?: string;
};

export const SEARCH_SCOPES: Array<{ key: SearchScope; label: string; field: keyof ProductListFilters }> = [
  { key: "all", label: "Everything", field: "q" },
  { key: "name", label: "Name", field: "productName" },
  { key: "xuid", label: "HKP tag", field: "xuid" },
  { key: "productId", label: "Product ID", field: "ProductId" },
  { key: "tags", label: "Tag", field: "tags" },
  { key: "color", label: "Colour", field: "color" },
];

export const ADDED_PRESETS: Array<{ key: AddedPreset; label: string }> = [
  { key: "any", label: "Any time" },
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "3m", label: "3 months" },
  { key: "custom", label: "Pick dates" },
];

const isoDay = (date: Date) => date.toISOString().slice(0, 10);

/** createdAfter / createdBefore for an "Added" window, relative to `now` so a saved "7 days" stays rolling. */
export function addedRange(added: AddedWindow | undefined, now = new Date()): { createdAfter: string; createdBefore: string } {
  const preset = added?.preset || "any";
  if (preset === "custom") {
    return { createdAfter: added?.from || "", createdBefore: added?.to || "" };
  }
  if (preset === "7d" || preset === "30d") {
    const start = new Date(now.getTime());
    start.setDate(start.getDate() - (preset === "7d" ? 7 : 30));
    return { createdAfter: isoDay(start), createdBefore: "" };
  }
  if (preset === "3m") {
    return { createdAfter: createdAfterForKpiMonths(3, now), createdBefore: "" };
  }
  return { createdAfter: "", createdBefore: "" };
}

export function addedLabel(added: AddedWindow | undefined): string {
  const preset = added?.preset || "any";
  if (preset === "custom") {
    const from = added?.from || "";
    const to = added?.to || "";
    if (from && to) return `${from} – ${to}`;
    if (from) return `Since ${from}`;
    if (to) return `Before ${to}`;
    return "Any time";
  }
  return ADDED_PRESETS.find((p) => p.key === preset)?.label || "Any time";
}

/**
 * Saved filters from before the redesign carry the KPI "Timeline" months and/or a date range,
 * not an `added` window. Map them once so the list keeps showing what the studio picked.
 */
export function migrateStoredFilters(stored: any, kpiMonths: number, now = new Date()): ProductListFilters {
  const base: ProductListFilters = stored && typeof stored === "object" && !Array.isArray(stored) ? { ...stored } : {};
  if (!base.added || typeof base.added !== "object") {
    if (kpiMonths === 1) base.added = { preset: "30d" };
    else if (kpiMonths === 3) base.added = { preset: "3m" };
    else if (kpiMonths > 0) base.added = { preset: "custom", from: createdAfterForKpiMonths(kpiMonths, now), to: "" };
    else if (base.createdAfter || base.createdBefore) {
      base.added = { preset: "custom", from: base.createdAfter || "", to: base.createdBefore || "" };
    } else base.added = { preset: "any" };
  }
  if (!SEARCH_SCOPES.some((scope) => scope.key === base.searchScope)) base.searchScope = "all";
  return withDerivedDates(base, now);
}

/** Keep the legacy createdAfter/createdBefore keys in step with `added` (other screens read them). */
export function withDerivedDates(filters: ProductListFilters, now = new Date()): ProductListFilters {
  return { ...filters, ...addedRange(filters.added, now) };
}

// ── status tabs ──

const STATUS_VIEWS = ["drafted", "published"];

export function statusTabFor(filters: ProductListFilters): StatusTab {
  if (filters.needsPhoto) return "needsPhoto";
  if (filters.view === "drafted") return "drafted";
  if (filters.inStock && filters.inStock.value === "true") return "inStock";
  if (filters.inStock && filters.inStock.value === "false") return "outOfStock";
  if (filters.view === "published") return "published";
  return "all";
}

/**
 * Picking a tab only changes the status dimension (view drafted/published, stock). Search, category,
 * price, colour and the rest stay. A transferred in/out view (set in Filters) is kept for the stock tabs
 * and for All, because the backend has one `view` param and drafted/published replace it.
 * Stock tabs list published pieces only, like their counts: a draft is not "out of stock", it is unfinished.
 */
export function applyStatusTab(input: ProductListFilters, tab: StatusTab): ProductListFilters {
  const filters: ProductListFilters = { ...input, needsPhoto: false, photoReason: "" };
  const transferred = filters.view && !STATUS_VIEWS.includes(filters.view) ? filters.view : "";
  if (tab === "needsPhoto") {
    // Needs photo covers drafts-turned-live with no photo and every open request: no status or stock narrowing.
    return { ...filters, view: transferred, inStock: "", needsPhoto: true };
  }
  if (tab === "drafted" || tab === "published") {
    return { ...filters, view: tab, inStock: "" };
  }
  if (tab === "inStock") {
    return { ...filters, view: transferred || "published", inStock: { label: "In stock", value: "true" } };
  }
  if (tab === "outOfStock") {
    return { ...filters, view: transferred || "published", inStock: { label: "Out of stock", value: "false" } };
  }
  return { ...filters, view: transferred, inStock: "" };
}

export type StatusCounts = Record<Exclude<StatusTab, "needsPhoto">, number>;

const finite = (value: unknown) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/** Counts for the tabs from /dashboard/dashboardwidgets (same maths as the old KPI strip). */
export function statusTabCounts(data: any, stockAdjust?: { inStock?: number; outOfStock?: number }): StatusCounts | null {
  if (!data) return null;
  const drafted = finite(data.draftProductCount);
  const published = finite(data.publishedProductCount);
  const transferredIn = finite(data.transferredInProductCount);
  return {
    all: drafted + published + transferredIn,
    drafted,
    published,
    inStock: Math.max(0, finite(data.inStockProductCount) + finite(stockAdjust?.inStock)),
    outOfStock: Math.max(0, finite(data.outOfStockProductCount) + finite(stockAdjust?.outOfStock)),
  };
}

// ── one search box ──

export function scopeField(scope: SearchScope | undefined): keyof ProductListFilters {
  return (SEARCH_SCOPES.find((s) => s.key === scope) || SEARCH_SCOPES[0]).field;
}

export function searchText(filters: ProductListFilters): string {
  const value = filters[scopeField(filters.searchScope)];
  return typeof value === "string" ? value : "";
}

/** Typing in the box writes only the field of the current "Look in" scope. */
export function setSearchText(filters: ProductListFilters, text: string): ProductListFilters {
  const field = scopeField(filters.searchScope);
  const next: ProductListFilters = { ...filters, [field]: text };
  if (field === "q" && next.includeAiSuggestions === undefined) next.includeAiSuggestions = true;
  return next;
}

/** Switching scope moves the typed text to the new field and clears the old one. */
export function setSearchScope(filters: ProductListFilters, scope: SearchScope): ProductListFilters {
  const text = searchText(filters);
  const oldField = scopeField(filters.searchScope);
  const next: ProductListFilters = { ...filters, searchScope: scope };
  if (text) {
    (next as any)[oldField] = "";
    (next as any)[scopeField(scope)] = text;
  }
  return next;
}

// ── craft & weave drill-in ──

export const CRAFT_LEVELS = ["artform", "type", "category", "subCategory", "weaveType"] as const;
export type CraftLevel = (typeof CRAFT_LEVELS)[number];

/** Picking a level clears the levels under it (they were narrowed by the old pick). */
export function setCraftLevel(filters: ProductListFilters, level: CraftLevel, value: Option | ""): ProductListFilters {
  const next: any = { ...filters, [level]: value };
  const index = CRAFT_LEVELS.indexOf(level);
  CRAFT_LEVELS.slice(index + 1).forEach((below) => {
    if (below === "category" && filters.category && (filters.category as CategoryOption).byName) return;
    next[below] = "";
  });
  return next;
}

// ── active filter chips ──

export type ActiveChip = { key: string; label: string };

const optionLabel = (value: any) => (value && typeof value === "object" && value.value ? String(value.label || value.value) : "");

export function priceLabel(price: Record<string, any> | undefined): string {
  if (!price) return "";
  const preset = price.preset || "ALL";
  if (preset === "ALL") return "";
  if (preset === "PRICED") return "Priced only";
  if (preset === "UNPRICED") return "Unpriced only";
  const rupees = (raw: string) => `₹${Number(raw).toLocaleString("en-IN")}`;
  const min = price.minSellPrice ? rupees(price.minSellPrice) : "";
  const max = price.maxSellPrice ? rupees(price.maxSellPrice) : "";
  if (min && max) return `${min} – ${max}`;
  if (max) return `Under ${max}`;
  if (min) return `Above ${min}`;
  return "";
}

const VIEW_CHIP_LABELS: Record<string, string> = {
  transferredIn: "Transferred in",
  transferredOut: "Transferred out",
};

/** Removable chips for everything narrowing the list except the search box text and the status tab. */
export function activeFilterChips(filters: ProductListFilters, opts: { priceEnabled?: boolean; stockChip?: boolean } = {}): ActiveChip[] {
  const chips: ActiveChip[] = [];
  const scope = scopeField(filters.searchScope);
  const push = (key: string, label: string) => label && chips.push({ key, label });
  push("category", optionLabel(filters.category));
  if (opts.priceEnabled) push("price", priceLabel(filters.price));
  if (scope !== "color" && filters.color) push("color", filters.color);
  if (filters.loomIds && filters.loomIds.length) push("loomIds", filters.loomIds.length === 1 ? "1 loom" : `${filters.loomIds.length} looms`);
  if (filters.typeTagIds && filters.typeTagIds.length) push("typeTagIds", `Types: ${filters.typeTagIds.length}`);
  if (filters.typeTagReview) push("typeTagReview", "Tags to review");
  if (filters.added && filters.added.preset !== "any") push("added", `Added: ${addedLabel(filters.added)}`);
  push("artform", optionLabel(filters.artform));
  push("type", optionLabel(filters.type));
  push("subCategory", optionLabel(filters.subCategory));
  push("weaveType", optionLabel(filters.weaveType));
  if (filters.view && VIEW_CHIP_LABELS[filters.view]) push("view", VIEW_CHIP_LABELS[filters.view]);
  if (opts.stockChip && filters.inStock && filters.inStock.value) push("inStock", filters.inStock.label);
  if (scope !== "xuid" && filters.xuid) push("xuid", `HKP tag: ${filters.xuid}`);
  if (scope !== "ProductId" && filters.ProductId) push("ProductId", `ID: ${filters.ProductId}`);
  if (scope !== "tags" && filters.tags) push("tags", `Tag: ${filters.tags}`);
  if (scope !== "productName" && filters.productName) push("productName", `Name: ${filters.productName}`);
  if (scope !== "q" && filters.q) push("q", `“${filters.q}”`);
  if (filters.hasDraftChanges) push("hasDraftChanges", "Edits not published");
  return chips;
}

export function removeFilterChip(filters: ProductListFilters, key: string): ProductListFilters {
  const next: any = { ...filters };
  switch (key) {
    case "price":
      next.price = { preset: "ALL" };
      break;
    case "added":
      next.added = { preset: "any" };
      break;
    case "hasDraftChanges":
      next.hasDraftChanges = false;
      break;
    case "typeTagIds":
      next.typeTagIds = [];
      break;
    case "loomIds":
      next.loomIds = [];
      break;
    case "typeTagReview":
      next.typeTagReview = false;
      break;
    case "view":
      next.view = "";
      break;
    case "artform":
    case "type":
    case "subCategory":
      return setCraftLevel(filters, key, "");
    case "category":
      next.category = "";
      next.subCategory = "";
      next.weaveType = "";
      break;
    default:
      next[key] = "";
  }
  return next;
}

/** Everything except the status tab, so "Clear all" in Filters keeps Drafts/Live. */
/** Clear all: every filter, the search and the status tab (Stock sits among the filters too). */
export function clearAllFilters(filters: ProductListFilters): ProductListFilters {
  return {
    searchScope: filters.searchScope || "all",
    includeAiSuggestions: true,
    added: { preset: "any" },
    price: { preset: "ALL" },
    view: "",
    inStock: "",
    productName: "",
    ProductId: "",
    xuid: "",
    tags: "",
    typeTagIds: [],
    typeTagReview: false,
    color: "",
    loomIds: [],
    q: "",
    category: "",
    artform: "",
    type: "",
    subCategory: "",
    weaveType: "",
    hasDraftChanges: false,
    needsPhoto: false,
    photoReason: "",
  };
}

/** True when the list is narrowed by a search, filter, tab or window (so an empty list is not a new studio). */
export function hasActiveProductFilters(filters: ProductListFilters): boolean {
  return Boolean(searchText(filters)) || statusTabFor(filters) !== "all" || activeFilterChips(filters, { priceEnabled: true }).length > 0;
}

// ── GET /product/products params ──

export type ProductSort = { name: string; order: "asc" | "desc" | "" };

export type QueryOptions = {
  page: number;
  pageSize: number;
  sort?: ProductSort;
  priceEnabled?: boolean;
  draftRevisionsEnabled?: boolean;
  now?: Date;
};

/** Params object for the `getProducts` saga (it turns these into the query string). */
export function buildProductQuery(filters: ProductListFilters, opts: QueryOptions) {
  const { createdAfter, createdBefore } = addedRange(filters.added, opts.now);
  const sort = opts.sort || { name: "", order: "" };
  const category = filters.category || undefined;
  const inStock = filters.inStock && filters.inStock.value;
  return {
    offset: Math.max(0, Math.floor(opts.page) || 0),
    limit: opts.pageSize,
    ProductsName: filters.productName || undefined,
    ProductId: filters.ProductId || undefined,
    artformId: optionValue(filters.artform),
    typeId: optionValue(filters.type),
    ...categoryParams(category as CategoryOption | undefined),
    price: opts.priceEnabled ? filters.price : undefined,
    subCategoryId: category && (category as CategoryOption).byName ? undefined : optionValue(filters.subCategory),
    weaveTypeId: optionValue(filters.weaveType),
    tags: filters.tags || undefined,
    typeTagIds: filters.typeTagIds && filters.typeTagIds.length ? filters.typeTagIds : undefined,
    typeTagReview: filters.typeTagReview ? "PENDING" : undefined,
    xuid: filters.xuid || undefined,
    color: filters.color || undefined,
    loomIds: filters.loomIds && filters.loomIds.length ? filters.loomIds : undefined,
    createdAfter: createdAfter || undefined,
    createdBefore: createdBefore || undefined,
    q: filters.q || undefined,
    view: filters.view || undefined,
    includeAiSuggestions: filters.includeAiSuggestions !== false,
    inStock: inStock === "true" ? true : inStock === "false" ? false : undefined,
    hasDraftChanges: opts.draftRevisionsEnabled && filters.hasDraftChanges ? true : undefined,
    needsPhotoUpdate: filters.needsPhoto ? true : undefined,
    photoUpdateReason: filters.needsPhoto && filters.photoReason ? filters.photoReason : undefined,
    sortBy: sort.order ? sort.name || undefined : undefined,
    orderBy: sort.order === "asc" ? "1" : sort.order === "desc" ? "-1" : undefined,
  };
}

function optionValue(value: any): string | undefined {
  return value && typeof value === "object" && value.value ? String(value.value) : undefined;
}

// ── paging ──

/** Most rows one reload asks for: a long scroll is cut back to this after an edit, never unbounded. */
export const PRODUCT_LIST_MAX_ROWS = 300;

/** Session key holding the product the user opened from the list, so Back can return to it. */
export const PRODUCT_LIST_BACK_KEY = "hkp.productList.back";

export type ProductListBack = {
  id: string;
  depth: number;
  /** The sort shown when the product was opened, so Back returns to the same order. */
  sort?: ProductSort;
  /** The list's scroll offset then: used when the product is no longer in the list. */
  scrollTop?: number;
};

/** What to remember when a product is opened: its id, the last page needed to show it again, the sort and scroll. */
export function backEntry(
  id: string | undefined,
  rows: Array<{ _id?: string }>,
  pageSize: number,
  extra: { sort?: ProductSort; scrollTop?: number } = {}
): ProductListBack | null {
  const index = id ? rows.findIndex((row) => row?._id === id) : -1;
  return id && index >= 0 ? { id, depth: depthOf(index + 1, pageSize), ...extra } : null;
}

/** Reads the saved entry; anything malformed is ignored. */
export function parseBack(raw: string | null): ProductListBack | null {
  try {
    const value = raw ? JSON.parse(raw) : null;
    if (!(value && typeof value.id === "string" && value.id && Number.isFinite(value.depth) && value.depth >= 0)) return null;
    const entry: ProductListBack = { id: value.id, depth: Math.floor(value.depth) };
    const sort = value.sort;
    if (sort && typeof sort === "object" && (sort.order === "asc" || sort.order === "desc")) {
      entry.sort = { name: String(sort.name || ""), order: sort.order };
    }
    if (Number.isFinite(value.scrollTop) && value.scrollTop > 0) entry.scrollTop = value.scrollTop;
    return entry;
  } catch {
    return null;
  }
}

/** What scrolls the list: the studio shell's main panel, or the page itself outside the shell. */
export function listScroller(doc: Document = document): Element | null {
  let el: Element | null = doc.querySelector(".pl-page");
  while (el && el !== doc.body) {
    if (/(auto|scroll)/.test(doc.defaultView?.getComputedStyle(el).overflowY || "")) return el;
    el = el.parentElement;
  }
  return doc.scrollingElement;
}

/** Rows to ask for when re-reading pages 0..depth in one request, so rows already scrolled into view stay. */
export function reloadLimit(depth: number, pageSize: number): number {
  const lastPage = Math.max(0, Math.floor(PRODUCT_LIST_MAX_ROWS / pageSize) - 1);
  return pageSize * (Math.min(Math.max(0, Math.floor(depth) || 0), lastPage) + 1);
}

/** Last page index covered by `rowCount` rows: the next page to fetch is this plus one. */
export function depthOf(rowCount: number, pageSize: number): number {
  return Math.max(0, Math.ceil(rowCount / pageSize) - 1);
}

/** Changes fields on one product in the shown rows (stock toggle, inline price) without a reload. */
export function patchRows<T extends { _id?: string }>(rows: T[], id: string, fields: Partial<T> & Record<string, unknown>): T[] {
  return rows.map((row) => (row?._id === id ? { ...row, ...fields } : row));
}

/** Adds the next page to the rows shown, skipping any product already in the list. */
export function appendRows<T extends { _id?: string }>(shown: T[], next: T[]): T[] {
  const seen = new Set(shown.map((row) => row?._id).filter(Boolean));
  return [...shown, ...next.filter((row) => !row?._id || !seen.has(row._id))];
}

